import { useEffect, useState, type FormEvent } from 'react';
import { IconSearch, IconX } from './Icons';

/**
 * Per-column filters for a list screen.
 *
 * Declarative: a screen supplies a FilterDef[] describing its columns and this renders the
 * right control for each. Everything is held as a string until it is sent, because that is
 * what a query string carries and it keeps "empty" unambiguous.
 *
 * Two rules the screens rely on:
 *
 *   Nothing is applied until Apply is pressed (or Enter). Filtering on every keystroke reads
 *   as lag on a table of any size, and a half-typed name is not a query anyone means.
 *
 *   Every supplied filter narrows the result. The server ANDs them, so adding a filter can
 *   only ever show fewer rows - which is what people expect and the opposite of what an OR
 *   would do.
 */

export type FilterOption = { value: string | number; label: string };

export type FilterDef = {
  /** Query key sent as filter[key]. Must match what the endpoint reads. */
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'date';
  options?: FilterOption[];
  placeholder?: string;
  /** Renders wider, for a description or a name. */
  wide?: boolean;
  /**
   * Value the screen opens on, and returns to on Reset. Blank when omitted.
   *
   * The control still shows it, so a default narrows the list visibly rather than silently -
   * the user can see why rows are missing and can widen it themselves.
   */
  defaultValue?: string;
};

export type FilterValues = Record<string, string>;

export function FilterBar({
  filters,
  values,
  onApply,
  busy = false,
}: {
  filters: FilterDef[];
  /** The values currently in effect, so Reset and re-render stay in step. */
  values: FilterValues;
  onApply: (next: FilterValues) => void;
  busy?: boolean;
}) {
  // Draft state: what is typed but not yet applied.
  const [draft, setDraft] = useState<FilterValues>(values);
  const [open, setOpen] = useState(false);

  // If the applied values change from outside (a Reset elsewhere, a route change), the draft
  // follows, or the boxes would show filters that are no longer in force.
  useEffect(() => setDraft(values), [values]);

  const activeCount = Object.values(values).filter((v) => v !== '').length;
  const dirty = JSON.stringify(draft) !== JSON.stringify(values);

  // Nothing to reset to when the applied values already are the defaults.
  const atDefaults = JSON.stringify(values) === JSON.stringify(defaultFilters(filters));

  function submit(event?: FormEvent) {
    event?.preventDefault();
    onApply(draft);
  }

  // Back to the screen's opening state, not to blank. Where a screen defaults to something
  // (Students opens on Active), blanking instead would quietly widen the list and Reset
  // would not restore what the user first saw.
  function reset() {
    const defaults = defaultFilters(filters);
    setDraft(defaults);
    onApply(defaults);
  }

  return (
    <form className="filters" onSubmit={submit}>
      <div className="filters__head">
        <button
          type="button"
          className="btn btn--sm btn--ghost filters__toggle"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          <IconSearch />
          Filters
          {activeCount > 0 && <span className="filters__count">{activeCount}</span>}
        </button>

        {!atDefaults && (
          <button type="button" className="btn btn--sm btn--ghost" onClick={reset}>
            <IconX />
            Reset
          </button>
        )}
      </div>

      {open && (
        <>
          <div className="filters__grid">
            {filters.map((filter) => {
              const id = `filter-${filter.key}`;
              const value = draft[filter.key] ?? '';

              const set = (next: string) =>
                setDraft((current) => ({ ...current, [filter.key]: next }));

              return (
                <div
                  className={filter.wide ? 'filters__field filters__field--wide' : 'filters__field'}
                  key={filter.key}
                >
                  <label htmlFor={id}>{filter.label}</label>

                  {filter.type === 'select' ? (
                    <select id={id} className="select" value={value} onChange={(e) => set(e.target.value)}>
                      <option value="">Any</option>
                      {(filter.options ?? []).map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={id}
                      className="input"
                      type={filter.type === 'date' ? 'date' : 'text'}
                      inputMode={filter.type === 'number' ? 'decimal' : undefined}
                      value={value}
                      placeholder={filter.placeholder}
                      onChange={(e) => set(e.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="filters__actions">
            <button type="submit" className="btn btn--sm btn--primary" disabled={busy}>
              {busy ? <><span className="btn__spin" />Searching…</> : 'Apply filters'}
            </button>

            {dirty && <span className="filters__hint">Press Enter to apply</span>}
          </div>
        </>
      )}
    </form>
  );
}

/** The opening values for a filter set: each filter's default, or blank. */
export function defaultFilters(filters: FilterDef[]): FilterValues {
  const values: FilterValues = {};
  filters.forEach((f) => { values[f.key] = f.defaultValue ?? ''; });
  return values;
}

/** Months, for the salary and fee period filters. */
export const MONTH_OPTIONS: FilterOption[] = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
].map((label, index) => ({ value: index + 1, label }));

export const ACTIVE_OPTIONS: FilterOption[] = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Suspended' },
];
